import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Wave, FavoriteFrequency } from '../../types';
import configService, { WaveConfig } from '../../services/configService';
import authService from '../../services/authService';
import analyticsService from '../../services/analyticsService';

interface ConfigManagerProps {
  currentWaves: Wave[];
  currentSpeed?: number;
  onLoadConfig: (waves: Wave[], speed?: number) => void;
  onConfigSaved?: () => void;
  mode?: 'presets' | 'saved' | 'save-dialog' | 'favorites';
  onClose?: () => void;
  onAddFavoriteToWorkspace?: (favorite: FavoriteFrequency) => void;
}

export const ConfigManager: React.FC<ConfigManagerProps> = ({
  currentWaves,
  currentSpeed = 1,
  onLoadConfig,
  onConfigSaved,
  mode = 'presets',
  onClose,
  onAddFavoriteToWorkspace
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [configs, setConfigs] = useState<WaveConfig[]>([]);
  const [presetConfigs, setPresetConfigs] = useState<WaveConfig[]>([]);
  const [favorites, setFavorites] = useState<FavoriteFrequency[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(mode === 'save-dialog');
  const [saveName, setSaveName] = useState('');
  const [saveDescription, setSaveDescription] = useState('');
  const [saveTags, setSaveTags] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [activeTab, setActiveTab] = useState<'saved' | 'presets'>('presets');
  const [searchQuery, setSearchQuery] = useState('');
  const [shareStatus, setShareStatus] = useState<{ configId: string; sharedId: string } | null>(null);
  const [copySuccess, setCopySuccess] = useState<string | null>(null);
  const [isLoadingPresets, setIsLoadingPresets] = useState(true);

  useEffect(() => {
    const loadPresets = async () => {
      setIsLoadingPresets(true);
      try {
        const presets = await configService.getPresetConfigurations();
        setPresetConfigs(presets);
      } catch (error) {
        console.error('Failed to load presets:', error);
        setPresetConfigs([]);
      } finally {
        setIsLoadingPresets(false);
      }
    };
    loadPresets();
  }, []);

  useEffect(() => {
    if (mode === 'favorites') {
      loadFavorites();
    }
  }, [mode]);

  useEffect(() => {
    if ((mode === 'saved' || isOpen) && authService.isAuthenticated()) {
      loadConfigs();
    }
  }, [mode, isOpen]);

  const loadFavorites = () => {
    const loadedFavorites = configService.listFavoriteFrequencies();
    setFavorites(loadedFavorites);
  };

  const loadConfigs = async () => {
    try {
      setIsLoading(true);
      // Only load user's personal configs, not community shared configs
      const result = await configService.listConfigs({ includeShared: false });
      setConfigs(result.configs);
    } catch (error) {
      console.error('Failed to load configurations:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    if (!saveName.trim()) return;

    try {
      setIsLoading(true);
      await configService.saveConfig({
        name: saveName.trim(),
        description: saveDescription.trim(),
        waves: currentWaves,
        speed: currentSpeed,
        tags: saveTags.split(',').map(tag => tag.trim()).filter(tag => tag),
        isPublic
      });
      
      // Track wave config save
      analyticsService.trackWaveConfigSave(`user-${Date.now()}`, true);
      analyticsService.trackFeatureUse('config_save', `waves:${currentWaves.length}_public:${isPublic}`);
      
      setSaveName('');
      setSaveDescription('');
      setSaveTags('');
      setIsPublic(false);
      setShowSaveDialog(false);
      onConfigSaved?.();
      await loadConfigs();
    } catch (error) {
      console.error('Failed to save configuration:', error);
      analyticsService.trackError('config_save_failed', error instanceof Error ? error.message : 'Unknown error');
      alert('Failed to save configuration. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoadConfig = async (config: WaveConfig) => {
    try {
      if (config.id && config.source) {
        const loadedConfig = await configService.loadConfig(config.id, config.source);
        onLoadConfig(loadedConfig.waves, loadedConfig.speed);
        analyticsService.trackWaveConfigLoad(config.id);
        analyticsService.trackFeatureUse('config_load', `source:${config.source}_waves:${loadedConfig.waves.length}`);
      } else {
        // Preset config
        onLoadConfig(config.waves, config.speed);
        analyticsService.trackFeatureUse('preset_config_load', `waves:${config.waves.length}`);
      }
      setIsOpen(false);
    } catch (error) {
      console.error('Failed to load configuration:', error);
      analyticsService.trackError('config_load_failed', error instanceof Error ? error.message : 'Unknown error');
      alert('Failed to load configuration. Please try again.');
    }
  };

  const handleDeleteConfig = async (configId: string, isShared: boolean = false) => {
    const message = isShared 
      ? 'Are you sure you want to delete this configuration? This will also remove the shared link.'
      : 'Are you sure you want to delete this configuration?';
    
    if (!confirm(message)) return;

    try {
      await configService.deleteConfig(configId);
      analyticsService.trackWaveConfigDelete(configId);
      analyticsService.trackFeatureUse('config_delete', `was_shared:${isShared}`);
      await loadConfigs();
    } catch (error) {
      console.error('Failed to delete configuration:', error);
      alert('Failed to delete configuration. Please try again.');
    }
  };

  const handleShareConfig = async (configId: string) => {
    try {
      const sharedId = await configService.shareConfig(configId);
      setShareStatus({ configId, sharedId });
      analyticsService.trackFeatureUse('config_share', `shared_id:${sharedId}`);
      await loadConfigs();
    } catch (error) {
      console.error('Failed to share configuration:', error);
      alert('Failed to create shareable link. Please try again.');
    }
  };

  const handleUnshareConfig = async (configId: string) => {
    if (!confirm('Remove this configuration from public sharing? (It will remain in your personal library)')) return;

    try {
      await configService.unshareConfig(configId);
      analyticsService.trackFeatureUse('config_unshare');
      await loadConfigs();
    } catch (error) {
      console.error('Failed to unshare configuration:', error);
      alert('Failed to unshare configuration. Please try again.');
    }
  };

  const handleCopyShareLink = (sharedId: string) => {
    const shareUrl = `${window.location.origin}?shared=${sharedId}`;
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopySuccess(sharedId);
      setTimeout(() => setCopySuccess(null), 2000);
    }).catch((error) => {
      console.error('Failed to copy link:', error);
      alert('Failed to copy link to clipboard.');
    });
  };

  const isAuthenticated = authService.isAuthenticated();

  // Mode: save-dialog - Show only the save dialog modal
  if (mode === 'save-dialog') {
    return createPortal(
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center p-4 overflow-y-auto" 
        style={{ zIndex: 9999 }}
        onClick={() => onClose?.()}
      >
        <div 
          className="bg-white rounded-lg p-6 w-full max-w-md my-8 shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">Save Configuration</h2>
            <button
              onClick={() => onClose?.()}
              className="text-gray-500 hover:text-gray-700 text-xl"
            >
              ×
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name *
              </label>
              <input
                type="text"
                value={saveName}
                onChange={(e) => setSaveName(e.target.value)}
                placeholder="My Configuration"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={saveDescription}
                onChange={(e) => setSaveDescription(e.target.value)}
                placeholder="Optional description..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tags
              </label>
              <input
                type="text"
                value={saveTags}
                onChange={(e) => setSaveTags(e.target.value)}
                placeholder="tag1, tag2, tag3"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">Separate tags with commas</p>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="isPublic"
                checked={isPublic}
                onChange={(e) => setIsPublic(e.target.checked)}
                className="mr-2"
              />
              <label htmlFor="isPublic" className="text-sm text-gray-700">
                Make this configuration public (others can see and use it)
              </label>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => onClose?.()}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  await handleSaveConfig();
                  onClose?.();
                }}
                disabled={isLoading || !saveName.trim()}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {isLoading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  // Mode: presets - Show preset list for right panel
  if (mode === 'presets') {
    return (
      <div className="space-y-1">
        {/* Filter/Search Box */}
        <div className="px-3 py-2 bg-white border-b border-gray-200 sticky top-0 z-10">
          <input
            type="text"
            placeholder="Filter presets..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        {isLoadingPresets ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
        <>
        {Object.entries(
          presetConfigs
            .filter(config => {
              if (!searchQuery.trim()) return true;
              const query = searchQuery.toLowerCase();
              const matchesName = config.name.toLowerCase().includes(query);
              const matchesDescription = config.description?.toLowerCase().includes(query);
              const matchesTags = config.tags?.some(tag => tag.toLowerCase().includes(query));
              return matchesName || matchesDescription || matchesTags;
            })
            .reduce((acc, config) => {
              const category = (config as any).category || 'Other';
              if (!acc[category]) acc[category] = [];
              acc[category].push(config);
              return acc;
            }, {} as Record<string, WaveConfig[]>)
        ).map(([category, configs]) => (
          <div key={category}>
            <div className="px-3 py-2 text-xs font-bold text-gray-500 uppercase tracking-wider bg-gray-50 border-b border-gray-200 sticky top-[60px] z-[5]">
              {category}
            </div>
            {configs.map((config, index) => (
              <div key={`${category}-${index}`} className="border-b border-gray-100 last:border-b-0 hover:bg-gray-50">
                <div className="flex justify-between items-start p-3">
                  <div className="flex-1 pr-2">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-medium text-gray-900 text-sm">{config.name}</h3>
                      <div className="text-xs text-gray-500">
                        {config.waves.length} wave{config.waves.length !== 1 ? 's' : ''}
                      </div>
                    </div>
                    <p className="text-xs text-gray-600 mb-2">{config.description}</p>
                    {config.tags && config.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {config.tags.map((tag, tagIndex) => (
                          <span key={tagIndex} className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => handleLoadConfig(config)}
                    className="px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded hover:bg-blue-700 transition-colors flex-shrink-0"
                  >
                    Load
                  </button>
                </div>
              </div>
            ))}
          </div>
        ))}
        </>
        )}
      </div>
    );
  }

  // Mode: saved - Show user's saved configurations
  if (mode === 'saved') {
    const filteredConfigs = configs.filter(config => {
      if (!searchQuery.trim()) return true;
      const query = searchQuery.toLowerCase();
      const matchesName = config.name.toLowerCase().includes(query);
      const matchesDescription = config.description?.toLowerCase().includes(query);
      const matchesTags = config.tags?.some(tag => tag.toLowerCase().includes(query));
      return matchesName || matchesDescription || matchesTags;
    });

    return (
      <div className="space-y-3">
        {/* Filter/Search Box */}
        <div className="px-4 pt-4 pb-2 bg-white sticky top-0 z-10">
          <input
            type="text"
            placeholder="Filter configurations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        
        <div className="px-4 pb-4 space-y-3">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : filteredConfigs.length === 0 ? (
          <div className="text-center py-8 text-gray-500 text-sm">
            {configs.length === 0 ? 'No saved configurations found.' : 'No configurations match your search.'}
          </div>
        ) : (
          filteredConfigs.map((config) => (
            <div key={config.id} className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50">
              <div className="flex justify-between items-start gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-1">
                    <h3 className="font-medium text-gray-900 text-sm truncate">{config.name}</h3>
                    {config.isPublic && (
                      <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded flex-shrink-0">
                        Public
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-600 mb-1">{config.description}</p>
                  <div className="flex items-center space-x-2 text-xs text-gray-500">
                    <span>{config.waves.length} waves</span>
                  </div>
                </div>
                <div className="flex flex-col space-y-1 flex-shrink-0">
                  <button
                    onClick={() => handleLoadConfig(config)}
                    className="px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700 text-xs whitespace-nowrap"
                  >
                    Load
                  </button>
                  {!(config as any).isShared ? (
                    <button
                      onClick={() => handleShareConfig(config.id!)}
                      className="px-2 py-1 bg-purple-600 text-white rounded hover:bg-purple-700 text-xs flex items-center gap-1 justify-center whitespace-nowrap"
                      title="Share"
                    >
                      <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                      </svg>
                    </button>
                  ) : (
                    <button
                      onClick={() => handleUnshareConfig(config.id!)}
                      className="px-2 py-1 bg-orange-600 text-white rounded hover:bg-orange-700 text-xs whitespace-nowrap"
                    >
                      Unshare
                    </button>
                  )}
                  <button
                    onClick={() => handleDeleteConfig(config.id!, (config as any).isShared)}
                    className="px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-xs whitespace-nowrap"
                  >
                    Delete
                  </button>
                </div>
              </div>
              {/* Share link display */}
              {(config as any).isShared && (config as any).sharedId && (
                <div className="mt-3 pt-3 border-t border-gray-200 flex items-center space-x-2">
                  <input
                    type="text"
                    readOnly
                    value={`${window.location.origin}?shared=${(config as any).sharedId}`}
                    className="flex-1 px-2 py-1 text-xs bg-gray-50 border border-gray-300 rounded truncate"
                    onClick={(e) => (e.target as HTMLInputElement).select()}
                  />
                  <button
                    onClick={() => handleCopyShareLink((config as any).sharedId)}
                    className="px-2 py-1 bg-purple-600 text-white text-xs rounded hover:bg-purple-700 flex items-center gap-1 flex-shrink-0"
                  >
                    {copySuccess === (config as any).sharedId ? (
                      <>
                        <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        ✓
                      </>
                    ) : (
                      <>
                        <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                        Copy
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          ))
        )}
        </div>
      </div>
    );
  }

  // Mode: favorites - Show user's favorite frequencies
  if (mode === 'favorites') {
    const filteredFavorites = favorites.filter(fav => {
      if (!searchQuery.trim()) return true;
      const query = searchQuery.toLowerCase();
      return fav.name.toLowerCase().includes(query) || 
             fav.freq.toString().includes(query);
    });

    const handleDeleteFavorite = (id: string) => {
      if (!confirm('Remove this favorite frequency?')) return;
      
      try {
        configService.deleteFavoriteFrequency(id);
        loadFavorites();
        analyticsService.trackFeatureUse('favorite_delete');
      } catch (error) {
        console.error('Failed to delete favorite:', error);
        alert('Failed to delete favorite. Please try again.');
      }
    };

    const handleAddFavoriteToWorkspace = (favorite: FavoriteFrequency) => {
      onAddFavoriteToWorkspace?.(favorite);
      analyticsService.trackFeatureUse('favorite_add_to_workspace', `freq:${favorite.freq}`);
    };

    return (
      <div className="space-y-3">
        {/* Filter/Search Box */}
        <div className="px-4 pt-4 pb-2 bg-white sticky top-0 z-10">
          <input
            type="text"
            placeholder="Filter favorites..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
          />
        </div>
        
        <div className="px-4 pb-4 space-y-3">
          {filteredFavorites.length === 0 ? (
            <div className="text-center py-8 text-gray-500 text-sm">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto mb-3 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
              {favorites.length === 0 ? (
                <>
                  <p className="mb-2">No favorite frequencies yet.</p>
                  <p className="text-xs">Click the heart icon on any wave card to save it!</p>
                </>
              ) : (
                'No favorites match your search.'
              )}
            </div>
          ) : (
            filteredFavorites.map((favorite) => (
              <div 
                key={favorite.id} 
                className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50 transition-colors"
                style={{ borderLeftWidth: '4px', borderLeftColor: favorite.color }}
              >
                <div className="flex justify-between items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24" style={{ color: favorite.color }}>
                        <path d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                      </svg>
                      <h3 className="font-medium text-gray-900 text-sm truncate">{favorite.name}</h3>
                    </div>
                    <div className="space-y-1 text-xs text-gray-600">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold" style={{ color: favorite.color }}>{favorite.freq} Hz</span>
                        <span>•</span>
                        <span>{favorite.amp}% amplitude</span>
                        <span>•</span>
                        <span>{favorite.phase}°</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-500">
                        <span>Channel: {favorite.channel || 'both'}</span>
                        {(favorite.autoPanRate || 0) > 0 && (
                          <>
                            <span>•</span>
                            <span>Auto-pan: {favorite.autoPanRate}Hz</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col space-y-1 flex-shrink-0">
                    <button
                      onClick={() => handleAddFavoriteToWorkspace(favorite)}
                      className="px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 text-xs whitespace-nowrap font-medium"
                    >
                      + Add Layer
                    </button>
                    <button
                      onClick={() => handleDeleteFavorite(favorite.id)}
                      className="px-3 py-1.5 bg-red-600 text-white rounded hover:bg-red-700 text-xs whitespace-nowrap"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    );
  }

  return null;
};